import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGetUsedData, apiUpdateFavorite } from "@api/userData";
import { USER_ROLE } from "@constants/index";
import type { UserDataRes } from "@/types/user";

export function useUserData() {
  return useQuery({
    queryKey: ["userData"],
    queryFn: async () => {
      const res = await apiGetUsedData();
      const userRoles = res.roles || [USER_ROLE.reader];
      return {
        ...res,
        roles: userRoles,
        isAdmin: userRoles.includes(USER_ROLE.admin),
      } as UserDataRes;
    },
  });
}

export function useUpdateFavorites() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiUpdateFavorite,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["userData"] });
      const previousUserData = queryClient.getQueryData<UserDataRes>(["userData"]);

      queryClient.setQueryData<UserDataRes>(["userData"], (old) => {
        if (!old) return old;
        return { ...old, preferences: { ...old.preferences, ...variables } };
      });

      return { previousUserData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousUserData)
        queryClient.setQueryData(["userData"], context.previousUserData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["userData"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}
